"use client";
/**
 * SANATAN DECODE — v13 "RIGGED". 21s @ 30fps, 1280x720.
 *
 * The one open item from the very first research pass: real Vox separates a figure into
 * parts so pieces move independently, while ours were single flat PNGs that could only
 * swing as a rigid slab.
 *
 * v13 rigs them WITHOUT new assets. The same PNG is drawn once per band, each band clipped
 * with inset() and rotated about its own bottom edge with a phase lag down the chain, so the
 * head leads, the torso follows and the base stays planted. Plus a slow vertical breathe.
 * Bands overlap 2.2% and amplitudes stay under ~2°, which is where the seam stops showing.
 *
 * Ground and layout are v10's on purpose — the ONLY variable against v10 is whether the
 * figures themselves feel alive. Judge that and nothing else.
 */
import React from "react";
import { AbsoluteFill, Audio, staticFile, interpolate } from "remotion";
import {
  INK, GOLD, MARIGOLD, KUMKUM, MUTED_C, deva, inter,
  f, useStep, useKeys, useSpringAt, W,
  Pano, Grain, Petals, Word, Row, Line, Beat, useCamera, useSwing,
  Rigged, RIG_HUMAN, RigBand, Prop, Disc, PersistentThread, Furniture,
} from "./sanatan/kit";

/** A manuscript is not a body: it hinges at its SPINE, so the bands pivot at the top-left
    and the amplitude climbs down the page like a page settling. */
const RIG_MANUSCRIPT: RigBand[] = [
  { from: 0, to: 40, amp: 0.5, phase: 0, pivot: 0 },
  { from: 36, to: 74, amp: 1.0, phase: 11, pivot: 0 },
  { from: 70, to: 100, amp: 1.6, phase: 22, pivot: 0 },
];

/** A rigged figure that still obeys the swing law on the way in and out. */
const RiggedProp: React.FC<{
  src: string; h: number; left: number; top: number; bands?: RigBand[]; speed?: number;
  inAt: number; outAt: number; from?: "left" | "right" | "top"; amp?: number; travel?: number;
}> = ({ src, h, left, top, bands, speed, ...cfg }) => {
  const style = useSwing(cfg);
  const frame = useStep();
  if (frame < f(cfg.inAt) - 4 || frame > f(cfg.outAt) + 30) return null;
  return (
    <div style={{ position: "absolute", left, top, ...style }}>
      <Rigged src={src} h={h} bands={bands} speed={speed} />
    </div>
  );
};

/* ══ PERSISTENT, AND NOW ALIVE ═════════════════════════════════
   One rishi from 3.9 to 10.2 — travelling and shrinking, never re-introduced (LAW 3) — but
   breathing and swaying in three parts the whole time he is resident. */
const PersistentRishi: React.FC = () => {
  const frame = useStep();
  const sIn = useSpringAt(3.9, 8, 90);
  const sOut = useSpringAt(10.2, 200, 70, 0.5);
  const left = useKeys([[3.9, 471], [5.6, 471], [6.3, 158], [10.2, 158]]);
  const top = useKeys([[3.9, 148], [5.6, 148], [6.3, 214], [10.2, 214]]);
  const h = useKeys([[3.9, 372], [5.6, 372], [6.3, 318], [10.2, 318]]);
  if (frame < f(3.7) || frame > f(10.9)) return null;
  const pend = Math.sin((frame - f(3.9)) / 34) * 1.3 * sIn;
  return (
    <div style={{
      position: "absolute", left, top,
      transform: `translate(${-260 * (1 - sIn)}px, ${-300 * (1 - sIn) + 200 * sOut}px) rotate(${13 * (1 - sIn) + pend - 26 * sOut}deg)`,
      transformOrigin: "50% -28%",
      opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
    }}>
      {/* slower than the modern man: age reads as a longer period, not smaller amplitude */}
      <Rigged src="sanatan/rishi.png" h={h} speed={44} breathe={0.008} />
    </div>
  );
};

const BeatA: React.FC = () => (
  <>
    <Disc at={0.0} out={3.6} x={880} y={300} r={250} color={MARIGOLD} />
    {/* the manuscript settles on its spine — the anchor is moving from frame 0 */}
    <div style={{ position: "absolute", left: 310, top: 368 }}>
      <RiggedProp src="sanatan/manuscript.png" h={300} left={0} top={0} bands={RIG_MANUSCRIPT} speed={38}
        inAt={-0.4} outAt={3.55} from="right" amp={12} travel={120} />
    </div>
    <div style={{ position: "absolute", left: 0, right: 0, top: 132 }}>
      <Row gap={14}>{[0, 1, 2, 3, 4, 5, 6].map((i) => <Word key={i} idx={i} size={38} color={MUTED_C} weight={600} underline={i === 2} outAt={3.6} />)}</Row>
      <div style={{ height: 16 }} />
      <Row>
        <Word idx={8} size={74} weight={900} outAt={3.62} />
        <Word idx={9} size={74} weight={900} outAt={3.64} />
        <Word idx={10} size={74} weight={900} color={KUMKUM} strike outAt={3.66} />
        <Word idx={11} size={74} weight={900} outAt={3.68} />
        <Word idx={12} size={74} weight={900} outAt={3.7} />
      </Row>
    </div>
  </>
);

const BeatB: React.FC = () => {
  const halo = useSpringAt(4.35, 11, 80);
  const gone = useSpringAt(5.5, 200, 70);
  return (
    <>
      <Disc at={3.9} out={5.5} x={640} y={300} r={280} color={GOLD} op={0.18} />
      <svg width={1280} height={720} style={{ position: "absolute", opacity: halo * 0.55 * (1 - gone) }}>
        <circle cx={612} cy={250} r={128 + halo * 34} fill="none" stroke={GOLD} strokeWidth={2} />
        <circle cx={612} cy={250} r={96 + halo * 22} fill="none" stroke={MARIGOLD} strokeWidth={1.5} />
      </svg>
      <div style={{ position: "absolute", left: 0, right: 0, top: 544, textAlign: "center" }}>
        <Row><Word idx={15} size={72} weight={900} color={KUMKUM} underline outAt={5.52} /></Row>
      </div>
    </>
  );
};

const ChainLabel: React.FC<{ text: string; at: number; x: number; y: number; size?: number; color?: string; weight?: number }> =
  ({ text, at, x, y, size = 21, color = MUTED_C, weight = 700 }) => {
    const sIn = useSpringAt(at, 9, 160, 0.8);
    const sOut = useSpringAt(10.2, 200, 80, 0.5);
    return (
      <div style={{
        position: "absolute", left: x - 90, top: y, width: 180, textAlign: "center",
        fontFamily: deva, fontSize: size, fontWeight: weight, color,
        textShadow: "0 0 22px rgba(255,251,240,0.98), 0 0 9px rgba(255,251,240,0.95)",
        opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
        transform: `translateY(${(1 - sIn) * 18 + sOut * 60}px) rotate(${6 * (1 - sIn)}deg)`,
        transformOrigin: "50% 140%",
      }}>{text}</div>
    );
  };

const CHAIN_X0 = 372, CHAIN_X1 = 928, CHAIN_Y = 396;
const CHAIN: [string, number, number][] = [
  ["पुत्र", 0.2, 6.80], ["पौत्र", 0.4, 6.98], ["प्रपौत्र", 0.6, 7.16], ["कई पीढ़ी", 0.8, 7.36],
];

const BeatC: React.FC = () => (
  <>
    <Disc at={5.8} out={10.25} x={300} y={330} r={230} color={KUMKUM} op={0.1} />
    <Disc at={6.6} out={10.25} x={1000} y={330} r={230} color={INK} op={0.09} />
    {/* the modern man fidgets faster than the rishi — same rig, shorter period */}
    <RiggedProp src="sanatan/modern_man.png" h={336} left={952} top={200} speed={24}
      inAt={7.0} outAt={10.25} from="right" amp={16} />
    <ChainLabel text="ऋषि" at={6.30} x={262} y={548} size={26} color={INK} weight={800} />
    {CHAIN.map(([t, frac, at]) => (
      <ChainLabel key={t} text={t} at={at} x={CHAIN_X0 + (CHAIN_X1 - CHAIN_X0) * frac} y={CHAIN_Y - 78} />
    ))}
    <ChainLabel text="आप" at={7.52} x={1034} y={548} size={26} color={KUMKUM} weight={800} />
    <div style={{ position: "absolute", left: 372, width: 556, top: 452, textAlign: "center" }}>
      <Row gap={14}>
        <Word idx={26} size={50} weight={800} outAt={10.2} />
        <Word idx={27} size={50} weight={800} outAt={10.22} />
        <Word idx={28} size={62} weight={900} color={KUMKUM} underline outAt={10.24} />
      </Row>
    </div>
  </>
);

const BeatDE: React.FC = () => {
  const tag = useSpringAt(11.61, 10, 150);
  const tagOut = useSpringAt(15.4, 200, 80);
  const archStyle = useSwing({ inAt: 11.0, outAt: 15.45, from: "left", amp: 7, travel: 150 });
  const AW = 900, AH = 344;
  const archPath = `M0 ${AH} L0 ${AH * 0.52}
    Q ${AW * 0.06} ${AH * 0.30} ${AW * 0.20} ${AH * 0.22}
    Q ${AW * 0.34} ${AH * 0.13} ${AW * 0.42} 0
    Q ${AW * 0.5} ${AH * 0.10} ${AW * 0.58} 0
    Q ${AW * 0.66} ${AH * 0.13} ${AW * 0.80} ${AH * 0.22}
    Q ${AW * 0.94} ${AH * 0.30} ${AW} ${AH * 0.52} L ${AW} ${AH} Z`;
  return (
    <>
      <Disc at={10.6} out={15.4} x={640} y={280} r={300} color={MARIGOLD} op={0.13} />
      {/* the couple sways as one unit but breathes — a pair reads wrong if the halves drift */}
      <div style={{ position: "absolute", left: 444, top: 62 }}>
        <RiggedProp src="sanatan/couple.png" h={252} left={0} top={0} speed={34}
          bands={[{ from: 0, to: 46, amp: 0.8, phase: 0 }, { from: 42, to: 100, amp: 0.3, phase: 12 }]}
          inAt={10.65} outAt={15.42} from="top" amp={11} travel={280} />
      </div>
      <div style={{ position: "absolute", left: 190, top: 336, ...archStyle }}>
        <svg width={AW} height={AH} style={{ overflow: "visible" }}>
          <path d={archPath} fill="#FFFCF2" stroke={GOLD} strokeWidth={2.5} />
        </svg>
        <div style={{ position: "absolute", left: 0, top: 116, width: AW, textAlign: "center" }}>
          <div style={{
            display: "inline-block", fontFamily: deva, fontSize: 22, fontWeight: 800, wordSpacing: 8,
            color: "#F7EFDD", background: INK, padding: "6px 28px", borderRadius: 3,
            opacity: Math.min(1, tag * 1.5) * (1 - tagOut),
            transform: `translateY(${(1 - tag) * 18}px) rotate(${6 * (1 - tag)}deg)`,
          }}>{W[34][2]}</div>
          <div style={{ height: 18 }} />
          <Row gap={18}>
            <Word idx={38} size={70} weight={900} outAt={15.4} />
            <Word idx={39} size={70} weight={900} outAt={15.42} />
            <Word idx={40} size={70} weight={900} color={KUMKUM} outAt={15.44} />
          </Row>
        </div>
      </div>
    </>
  );
};

const BeatF: React.FC = () => {
  const frame = useStep();
  const CX = 448, PY = 232, CY = 388;
  const pA = useSpringAt(15.9, 9, 140);
  const pB = useSpringAt(16.2, 9, 140);
  const kid = useSpringAt(17.15, 8, 150);
  const term = useSpringAt(17.43, 10, 120);
  const bob = Math.sin(frame / 24) * 5;
  const Allele: React.FC<{ x: number; fill: string; opacity?: number }> = ({ x, fill, opacity = 1 }) => (
    <rect x={x - 7} y={-20} width={14} height={40} rx={7} fill={fill} opacity={opacity} />
  );
  const Carrier: React.FC<{ x: number; s: number; ph: number }> = ({ x, s, ph }) => (
    <g opacity={s} transform={`translate(${x},${PY + Math.sin((frame + ph) / 26) * 6}) scale(${0.8 + s * 0.2}) rotate(${8 * (1 - s)})`}>
      <circle r={44} fill="#FDF8EC" stroke={INK} strokeWidth={4} />
      <Allele x={-15} fill={INK} opacity={0.22} />
      <Allele x={15} fill={KUMKUM} />
    </g>
  );
  return (
    <>
      <Disc at={15.8} out={22} x={1010} y={330} r={270} color={INK} op={0.09} />
      {/* the helix gets a WAVE rig — six thin bands, long phase lag, so it undulates */}
      <div style={{ position: "absolute", left: 940, top: 110 }}>
        <RiggedProp src="sanatan/dna.png" h={520} left={0} top={0} speed={26} inAt={15.85} outAt={22} from="right" amp={14}
          bands={Array.from({ length: 6 }, (_, i) => ({ from: i * 16.6, to: (i + 1) * 16.6 + 2, amp: 1.4, phase: i * 7, pivot: 50 }))} />
      </div>
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Carrier x={CX - 128} s={pA} ph={0} />
        <Carrier x={CX + 128} s={pB} ph={40} />
        <g opacity={kid} transform={`translate(${CX},${CY + bob}) scale(${0.78 + kid * 0.22}) rotate(${-10 * (1 - kid)})`}>
          <circle r={46} fill={KUMKUM} opacity={0.14} />
          <circle r={46} fill="none" stroke={KUMKUM} strokeWidth={5} />
          <Allele x={-15} fill={KUMKUM} />
          <Allele x={15} fill={KUMKUM} />
        </g>
      </svg>
      <div style={{
        position: "absolute", left: 84, top: 486, width: 730, textAlign: "center",
        opacity: Math.min(1, term * 1.4),
        transform: `translateY(${(1 - term) * 34}px) rotate(${4 * (1 - term)}deg)`,
      }}>
        <div style={{ fontFamily: inter, fontSize: 42, fontWeight: 800, letterSpacing: 6, color: KUMKUM }}>CONSANGUINITY</div>
        <div style={{ height: 8 }} />
        <div style={{ fontFamily: deva, fontSize: 25, fontWeight: 700, color: INK, opacity: 0.8 }}>विज्ञान ने इसे एक नाम दिया।</div>
      </div>
    </>
  );
};

export const SanatanDecodeGotraV13: React.FC = () => {
  const cam = useCamera();
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
      <Audio src={staticFile("sanatan/gotra.mp3")} />
      <Pano />
      <AbsoluteFill style={{ transform: cam }}>
        <Beat from={0} to={3.75}><BeatA /></Beat>
        <Beat from={3.9} to={5.6}><BeatB /></Beat>
        <Beat from={5.8} to={10.4}><BeatC /></Beat>
        <Beat from={10.6} to={15.6}><BeatDE /></Beat>
        <Beat from={15.8} to={21.1}><BeatF /></Beat>
        <PersistentRishi />
        <PersistentThread />
      </AbsoluteFill>
      <Petals />
      <Grain />
      <Furniture />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
